import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import React from "react";
import {
    ModelResultFeedback,
    ModelResultFeedbackService,
    SaveModelResultFeedbackRequest,
} from "services/ModelResultFeedbackService";
import { useStore } from "utils/useStore";
import { AcxLLMReasoningFeedbackProps, FeedbackState } from "./types";

/**
 * Real, service-backed replacement for useReasoningFeedbackStub. Loads the
 * current user's existing feedback for one model result, and points onSubmit /
 * onDelete at ModelResultFeedbackService. Returns the same props shape as the
 * stub, so nothing inside AcxLLMReasoningFeedback / AcxLLMReasoning changes.
 *
 * Identity of a model result is (audioMetadataId, classifierId) -- the same keys
 * the backend uses. On the conversation surface both come off the classifier;
 * pass evaluationId as well when the feedback is given inside an evaluation.
 */
export interface UseReasoningFeedbackArgs {
    audioMetadataId: string;
    classifierId: string;
    evaluationId?: string | null;
    /** Skip loading/saving entirely, e.g. the user can't give feedback. */
    enabled?: boolean;
}

const NO_FEEDBACK: FeedbackState = {
    agreed: null,
    note: null,
    status: "idle",
    submittedAt: null,
};

function toSubmittedState(feedback: ModelResultFeedback): FeedbackState {
    return {
        agreed: feedback.agreed,
        note: feedback.note,
        status: "submitted",
        submittedAt: feedback.submittedAt,
    };
}

export function useReasoningFeedback(
    args: UseReasoningFeedbackArgs,
): AcxLLMReasoningFeedbackProps {
    const { audioMetadataId, classifierId, evaluationId, enabled = true } = args;

    const service = React.useMemo(() => new ModelResultFeedbackService(), []);
    const messageStore = useStore(MessageStore);
    const [state, setState] = React.useState<FeedbackState>(NO_FEEDBACK);

    // Latest state, read by the async callbacks without going stale so they can
    // stay referentially stable.
    const stateRef = React.useRef(state);
    stateRef.current = state;

    // The saved row's id (for update/delete). null = nothing persisted yet.
    const savedId = React.useRef<string | null>(null);
    // What Cancel reverts to: the last values that were actually persisted.
    const lastSubmitted = React.useRef<FeedbackState>(NO_FEEDBACK);

    // Load any existing feedback for this result on mount.
    React.useEffect(() => {
        if (!enabled || !audioMetadataId || !classifierId) return;

        let cancelled = false;
        service
            .getForInteraction(audioMetadataId)
            .then((rows) => {
                if (cancelled) return;
                const mine = rows.find((r) => r.classifierId === classifierId);
                if (!mine) return;
                savedId.current = mine.id;
                const loaded = toSubmittedState(mine);
                lastSubmitted.current = loaded;
                setState(loaded);
            })
            .catch(() => {
                // Leave as NO_FEEDBACK -- a load failure shouldn't block the UI.
            });

        return () => {
            cancelled = true;
        };
    }, [service, enabled, audioMetadataId, classifierId]);

    const setVote = React.useCallback((agreed: boolean) => {
        setState((prev) => ({ ...prev, agreed, status: "idle" }));
    }, []);

    const onSubmit = React.useCallback(async () => {
        const current = stateRef.current;
        const request: SaveModelResultFeedbackRequest = {
            audioMetadataId,
            classifierId,
            evaluationId: evaluationId ?? null,
            agreed: current.agreed === true,
            note: current.note?.trim() || null,
        };

        setState((prev) => ({ ...prev, status: "saving" }));
        try {
            const saved = await service.saveFeedback(request);
            savedId.current = saved.id;
            const submitted = toSubmittedState(saved);
            lastSubmitted.current = submitted;
            setState(submitted);
        } catch {
            // Surface the failure and return to the composer so the user can retry.
            messageStore.logError("Couldn't save your feedback. Please try again.");
            setState((prev) => ({ ...prev, status: "idle" }));
        }
    }, [service, messageStore, audioMetadataId, classifierId, evaluationId]);

    const onDelete = React.useCallback(async () => {
        const id = savedId.current;
        if (id) {
            try {
                await service.deleteFeedback(id);
            } catch {
                // Keep the current state if the delete failed.
                messageStore.logError("Couldn't remove your feedback. Please try again.");
                return;
            }
        }
        savedId.current = null;
        lastSubmitted.current = NO_FEEDBACK;
        setState(NO_FEEDBACK);
    }, [service, messageStore]);

    return {
        state,
        onAgree: React.useCallback(() => setVote(true), [setVote]),
        onDisagree: React.useCallback(() => setVote(false), [setVote]),
        onNoteChange: React.useCallback((note: string) => {
            setState((prev) => ({ ...prev, note }));
        }, []),
        onSubmit,
        onEdit: React.useCallback(() => {
            setState((prev) => ({ ...prev, status: "idle" }));
        }, []),
        onCancel: React.useCallback(() => {
            setState(lastSubmitted.current);
        }, []),
        onDelete,
    };
}
